import {
  createContext,
  useContext,
  useCallback,
  type ReactNode,
} from 'react'
import { trackEvent } from '../lib/analytics'
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import {
  fetchCart,
  addCartItem,
  updateCartItem,
  removeCartItem,
  clearCartRemote,
  type ServerCart,
  type ServerCartItem,
} from '../api/cart'

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * CartItem shape exposed to the rest of the app.
 * Derived from ServerCartItem to maintain the same interface the CartPage
 * and product detail pages already consume.
 */
export interface CartItem {
  normalizedMpn: string
  slug: string
  displayTitle: string
  /** EUR ex. VAT — null means "price on request" */
  effectivePrice: string | null
  effectivePriceIncVat: string | null
  primaryImageUrl: string | null
  quantity: number
  moq: number
  lead_time_days?: number | null
}

interface CartContextValue {
  items: CartItem[]
  itemCount: number
  subtotalExVat: number
  vatAmount: number
  totalIncVat: number
  hasUnpricedItems: boolean
  isLoading: boolean
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void
  removeItem: (mpn: string) => void
  updateQuantity: (mpn: string, quantity: number) => void
  clearCart: () => void
}

// ── Constants ─────────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export const CART_QUERY_KEY = ['cart'] as const
const VAT_RATE = 0.21

// ── Helpers ──────────────────────────────────────────────────────────────────

function serverItemToLocal(item: ServerCartItem): CartItem {
  const priceIncVat = item.unit_price !== null
    ? (parseFloat(item.unit_price) * (1 + VAT_RATE)).toFixed(2)
    : null
  return {
    normalizedMpn: item.normalized_mpn,
    slug: item.slug,
    displayTitle: item.display_title,
    effectivePrice: item.unit_price,
    effectivePriceIncVat: priceIncVat,
    primaryImageUrl: item.primary_image_url,
    quantity: item.quantity,
    moq: item.moq,
    lead_time_days: item.lead_time_days ?? null,
  }
}

function serverCartToContext(cart: ServerCart): Pick<CartContextValue, 'items' | 'itemCount' | 'subtotalExVat' | 'vatAmount' | 'totalIncVat' | 'hasUnpricedItems'> {
  return {
    items: cart.items.map(serverItemToLocal),
    itemCount: cart.item_count,
    subtotalExVat: parseFloat(cart.subtotal_ex_vat),
    vatAmount: parseFloat(cart.vat_amount),
    totalIncVat: parseFloat(cart.total_inc_vat),
    hasUnpricedItems: cart.has_unpriced_items,
  }
}

const emptyCartValues: Pick<CartContextValue, 'items' | 'itemCount' | 'subtotalExVat' | 'vatAmount' | 'totalIncVat' | 'hasUnpricedItems'> = {
  items: [],
  itemCount: 0,
  subtotalExVat: 0,
  vatAmount: 0,
  totalIncVat: 0,
  hasUnpricedItems: false,
}

// ── Context ──────────────────────────────────────────────────────────────────

const CartContext = createContext<CartContextValue | null>(null)

// eslint-disable-next-line react-refresh/only-export-components
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}

// ── Optimistic update helpers ─────────────────────────────────────────────────

/**
 * Build an optimistic cart update for a quantity change.
 * Used by addItem and updateQuantity to give instant feedback.
 */
function applyOptimisticQtyChange(
  previous: ServerCart | undefined,
  normalizedMpn: string,
  deltaOrAbsolute: number,
  mode: 'delta' | 'absolute',
): ServerCart | undefined {
  if (!previous) return previous

  const existingIdx = previous.items.findIndex(i => i.normalized_mpn === normalizedMpn)
  let newItems: ServerCartItem[]

  if (existingIdx >= 0) {
    const existing = previous.items[existingIdx]
    const newQty = mode === 'delta'
      ? existing.quantity + deltaOrAbsolute
      : deltaOrAbsolute
    if (newQty <= 0) {
      newItems = previous.items.filter(i => i.normalized_mpn !== normalizedMpn)
    } else {
      newItems = previous.items.map((i, idx) =>
        idx === existingIdx ? { ...i, quantity: newQty } : i
      )
    }
  } else {
    // Item not in cart yet — don't optimistically add (we don't have the full data)
    newItems = previous.items
  }

  const subtotalExVat = newItems.reduce((sum, i) => {
    if (i.unit_price === null) return sum
    return sum + parseFloat(i.unit_price) * i.quantity
  }, 0)
  const vatAmount = subtotalExVat * 0.21
  const totalIncVat = subtotalExVat + vatAmount
  const itemCount = newItems.reduce((sum, i) => sum + i.quantity, 0)

  return {
    ...previous,
    items: newItems,
    item_count: itemCount,
    subtotal_ex_vat: subtotalExVat.toFixed(4),
    vat_amount: vatAmount.toFixed(4),
    total_inc_vat: totalIncVat.toFixed(4),
    has_unpriced_items: newItems.some(i => i.unit_price === null),
  }
}

/**
 * Build an optimistic cart update for ADDING an item. Unlike a plain quantity
 * change, this inserts a brand-new line when the product isn't in the cart yet —
 * so the header badge and totals update instantly instead of sitting stale until
 * the server round-trip returns. onSuccess still reconciles with server truth.
 */
function applyOptimisticAdd(
  previous: ServerCart | undefined,
  item: Omit<CartItem, 'quantity'>,
  quantity: number,
): ServerCart | undefined {
  if (!previous) return previous

  const existingIdx = previous.items.findIndex(i => i.normalized_mpn === item.normalizedMpn)
  let newItems: ServerCartItem[]

  if (existingIdx >= 0) {
    newItems = previous.items.map((i, idx) =>
      idx === existingIdx ? { ...i, quantity: i.quantity + quantity } : i
    )
  } else {
    const unitPrice = item.effectivePrice
    newItems = [
      ...previous.items,
      {
        normalized_mpn: item.normalizedMpn,
        slug: item.slug,
        display_title: item.displayTitle,
        unit_price: unitPrice,
        line_subtotal: unitPrice !== null ? (parseFloat(unitPrice) * quantity).toFixed(4) : null,
        quantity,
        moq: item.moq,
        primary_image_url: item.primaryImageUrl,
        lead_time_days: item.lead_time_days ?? null,
      },
    ]
  }

  const subtotalExVat = newItems.reduce((sum, i) => {
    if (i.unit_price === null) return sum
    return sum + parseFloat(i.unit_price) * i.quantity
  }, 0)
  const vatAmount = subtotalExVat * VAT_RATE
  const totalIncVat = subtotalExVat + vatAmount
  const itemCount = newItems.reduce((sum, i) => sum + i.quantity, 0)

  return {
    ...previous,
    items: newItems,
    item_count: itemCount,
    subtotal_ex_vat: subtotalExVat.toFixed(4),
    vat_amount: vatAmount.toFixed(4),
    total_inc_vat: totalIncVat.toFixed(4),
    has_unpriced_items: newItems.some(i => i.unit_price === null),
  }
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function CartProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  // Fetch the cart from the server.  On SSR / network-less environments this
  // gracefully falls back to an empty cart.
  const { data: serverCart, isLoading } = useQuery({
    queryKey: CART_QUERY_KEY,
    queryFn: fetchCart,
    // Don't throw; empty cart is the safe fallback
    throwOnError: false,
    // Keep the cart fresh for the lifetime of the page, refetch on window focus
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
  })

  const derived = serverCart ? serverCartToContext(serverCart) : emptyCartValues

  // ── Mutations ───────────────────────────────────────────────────────────────

  const addMutation = useMutation({
    mutationFn: ({ item, quantity }: { item: Omit<CartItem, 'quantity'>; quantity: number }) =>
      addCartItem(item.normalizedMpn, quantity),
    onMutate: async ({ item, quantity }) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<ServerCart>(CART_QUERY_KEY)
      queryClient.setQueryData<ServerCart>(CART_QUERY_KEY, prev =>
        applyOptimisticAdd(prev, item, quantity)
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, ctx.previous)
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(CART_QUERY_KEY, data)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ mpn, quantity }: { mpn: string; quantity: number }) =>
      updateCartItem(mpn, quantity),
    onMutate: async ({ mpn, quantity }) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<ServerCart>(CART_QUERY_KEY)
      queryClient.setQueryData<ServerCart>(CART_QUERY_KEY, prev =>
        applyOptimisticQtyChange(prev, mpn, quantity, 'absolute')
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, ctx.previous)
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(CART_QUERY_KEY, data)
    },
  })

  const removeMutation = useMutation({
    mutationFn: ({ mpn }: { mpn: string }) => removeCartItem(mpn),
    onMutate: async ({ mpn }) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<ServerCart>(CART_QUERY_KEY)
      queryClient.setQueryData<ServerCart>(CART_QUERY_KEY, prev =>
        applyOptimisticQtyChange(prev, mpn, 0, 'absolute')
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, ctx.previous)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
    },
  })

  const clearMutation = useMutation({
    mutationFn: clearCartRemote,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<ServerCart>(CART_QUERY_KEY)
      queryClient.setQueryData<ServerCart>(CART_QUERY_KEY, prev => prev
        ? { ...prev, items: [], item_count: 0, subtotal_ex_vat: '0.0000', vat_amount: '0.0000', total_inc_vat: '0.0000', has_unpriced_items: false }
        : prev
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, ctx.previous)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
    },
  })

  // ── Public API ───────────────────────────────────────────────────────────────

  const addItem = useCallback(
    (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
      const { quantity: requestedQty, ...rest } = item
      const quantity = requestedQty ?? Math.max(1, item.moq)
      addMutation.mutate({ item: rest, quantity })
      trackEvent('Add to Cart', { sku: item.normalizedMpn })
    },
    [addMutation],
  )

  const removeItem = useCallback(
    (mpn: string) => removeMutation.mutate({ mpn }),
    [removeMutation],
  )

  const updateQuantity = useCallback(
    (mpn: string, quantity: number) => {
      const item = derived.items.find(i => i.normalizedMpn === mpn)
      const moq = item?.moq ?? 1
      const safeQty = quantity <= 0 ? 0 : Math.max(moq, Math.floor(quantity))
      if (safeQty === 0) {
        removeMutation.mutate({ mpn })
      } else {
        updateMutation.mutate({ mpn, quantity: safeQty })
      }
    },
    [derived.items, updateMutation, removeMutation],
  )

  const clearCart = useCallback(
    () => clearMutation.mutate(),
    [clearMutation],
  )

  return (
    <CartContext.Provider
      value={{
        ...derived,
        isLoading,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

/**
 * Exported for use in tests and SSR: prefetch the cart into the query cache.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function prefetchCart(queryClient: QueryClient): Promise<void> {
  return queryClient.prefetchQuery({
    queryKey: CART_QUERY_KEY,
    queryFn: fetchCart,
  })
}
